import { useAuth } from '../../../hooks/useAuth';
import type { Climb } from '../../../types/climb';

/**
 * The roles that own a place's climbs — the same pair the server writes them
 * through (`Location#can_manage_climbs?`). Asked on the client as well so the
 * page shows the fields it is actually allowed to save, rather than offering
 * them and failing on the way out.
 */
const MANAGING_ROLES = ['admin', 'editor'];

/**
 * Everyone who works on a place's climbs, the setter among them
 * (`Location#climb_staff?`): the people who keep its roster of setters.
 */
const CLIMB_STAFF_ROLES = [...MANAGING_ROLES, 'routesetter'];

export interface ClimbEditorAccess {
  /** Signed in at all: nothing on this page can be saved otherwise. */
  signedIn: boolean;
  /**
   * Runs the place. The one role that may put a climb up with no photograph —
   * entering a wall as a list and leaving the pictures to whoever climbs it.
   */
  administers: boolean;
  /**
   * May write the climb's own fields: the place's climb staff, or whoever put
   * this climb up.
   */
  manages: boolean;
  /**
   * The reader's user id when they have no climb role at the place: a climber,
   * who credits a climb from the setters that already exist or to themselves
   * (see RoutesetterPicker's `memberUserId`). Null for the place's climb staff,
   * who keep its roster, and for a visitor.
   */
  memberUserId: string | null;
  /**
   * Here only to give a climb the photograph it never had. Anybody may, and what
   * they may save with it is the picture, the drawing on it and a name for a
   * climb that has none — see the controller's #photo_contribution_attributes.
   */
  contributing: boolean;
}

/**
 * What the reader is allowed to do to the climb on the editor page.
 *
 * Two answers rather than one, because a photo-less climb is worked on by two
 * different people: the gym's staff, who own everything it says about itself,
 * and the climber standing in front of the wall, who owns the one thing missing.
 * Which of them is here decides what the page asks for and what the save sends.
 */
export function useClimbEditorAccess(
  locationSlug: string | undefined,
  climb: Climb | null
): ClimbEditorAccess {
  const { user } = useAuth();

  const staff = user?.staff?.find((entry) => entry.gym_slug === locationSlug);
  const confirmed = staff?.status === 'confirmed';
  const administers = !!confirmed && staff.staff_roles.includes('admin');
  const climbStaff =
    !!confirmed && staff.staff_roles.some((role) => CLIMB_STAFF_ROLES.includes(role));
  const manages =
    (!!confirmed && staff.staff_roles.some((role) => MANAGING_ROLES.includes(role))) ||
    (!!climb?.created_by && !!user && climb.created_by.id === String(user.id));

  return {
    signedIn: !!user,
    administers,
    manages,
    memberUserId: user && !climbStaff ? String(user.id) : null,
    // A board problem is drawn on the wall's own photograph, so it has none to
    // be missing; and a climb whose picture is already there is the gym's again.
    contributing:
      !!user && !manages && !!climb && !climb.has_image && climb.type !== 'BoardClimb',
  };
}
